import useAxios from '../../hooks/useAxios'
import { DriveItem } from '@microsoft/microsoft-graph-types'
import {
    DocumentNodeType,
    driveItemListProperties,
    getDocumentNodeType,
    mapDriveItemToOverviewItemDto,
} from '../../helpers/documentHelper'
import {
    DataPromise,
    Destination,
    getDataEmptyState,
    getDataLoadedState,
    getDataLoadingState,
    getDataRejectedState,
    GraphError,
    IGraphArrayResponse,
} from '../../context/AxiosContext'
import { useEffect, useState } from 'react'
import { DocumentOverviewItemDto, IDocumentStackItemDto, IFolderOverviewItemDto } from '../../config/documentConfig'
import { AxiosResponse } from 'axios'

type ReturnType = DataPromise<IFolderOverviewItemDto, undefined, GraphError>

function mapDriveItemsToOverviewItemDto(
    items: DriveItem[],
    stack: IDocumentStackItemDto[] = [],
    parent?: DriveItem,
): DocumentOverviewItemDto[] {
    return items
        .filter(item =>
            parent
                ? item.parentReference.id === parent.id
                : // Make sure that this item is a child of the drive root.
                  !items.find(_item => _item.id === item.parentReference.id),
        )
        .map(item => ({
            ...mapDriveItemToOverviewItemDto(item),
            stack,
            children:
                getDocumentNodeType(item) === DocumentNodeType.Folder
                    ? mapDriveItemsToOverviewItemDto(items, [...stack, { id: item.id, name: item.name }], item)
                    : undefined,
        }))
}

const mapResponse = (response: IGraphArrayResponse<DriveItem>, rootId: string): IFolderOverviewItemDto =>
    mapDriveItemsToOverviewItemDto(response.data.value).find(item => item.id === rootId) as IFolderOverviewItemDto

export default function useFileDocuments(reference: string): ReturnType {
    const axios = useAxios()
    const [root, setRoot] = useState<ReturnType>(getDataEmptyState())

    useEffect(() => {
        if (reference) {
            setRoot(getDataLoadingState(undefined, root.data))
            axios
                .get('/file/document-folder/root/id', { params: { references: [reference] } })
                .then((response: AxiosResponse<{ [key: string]: string }>) => response.data[reference])
                .then((rootId: string) =>
                    axios
                        .get(`/items/${rootId}/search(q='')`, {
                            baseURL: Destination.Documents,
                            params: { $select: driveItemListProperties.join(',') },
                            headers: { SuppressErrors: 'true' },
                        })
                        .then((response: IGraphArrayResponse<DriveItem>) => {
                            setRoot(getDataLoadedState(mapResponse(response, rootId)))
                        }),
                )
                .catch(error => setRoot(getDataRejectedState(undefined, undefined, error)))
        } else {
            setRoot(getDataEmptyState())
        }
    }, [reference])

    return root
}
